import { IReportEvents } from '@infotech-soe/merps/types/Reports';
import {
  Stimulsoft,
  StiViewer,
} from '@infotech-soe/stimulsoft-reports-js/Scripts/stimulsoft.viewer';
import React, { useEffect, useRef, useState } from 'react';

import languages, { LanguageCode } from './localization';
import { ViewerOptionValues } from './types';
import {
  addPrintFooter,
  serializeDataForStiReport,
  setLocalization,
  updateViewerOptions,
} from './utils';

const Localization = Stimulsoft.Base.Localization.StiLocalization;

export interface StimulsoftViewerProps {
  /** Id attribute for root node */
  id?: string;
  /** Appearance properties */
  appearance?: ViewerOptionValues<Stimulsoft.Viewer.StiAppearanceOptions>;
  /** Export properties */
  exports?: Partial<Stimulsoft.Viewer.StiExportsOptions>;
  /** Component height in % or px. Default 100% */
  height?: string;
  /** Interface language like 'uk' */
  language?: LanguageCode;
  /** Raw report template (formally content of *.mrt file). */
  reportTemplate?: string | number[];
  /** Raw report data (formally content of *.json file) */
  reportData?: string | number[];
  /** Toolbar options */
  toolbar?: ViewerOptionValues<Stimulsoft.Viewer.StiToolbarOptions>;
  /** Variables for template */
  variables?: Record<string, any>;
  /** Component width in % or px. Default 100% */
  events?: IReportEvents;
  width?: string;
  isDarkMode?: boolean;
  /** Callback  */
  onError?: (error: any) => void;
  onRendered?: () => void;
  tabIndex?: number;
  parseDates?: boolean;
  printedBy?: string;
  showPrintInfo?: boolean;
}

export const StimulsoftViewer: React.FC<StimulsoftViewerProps> = ({
  id,
  language,
  reportTemplate,
  reportData,
  variables,
  events,
  tabIndex,
  parseDates = true,
  printedBy,
  showPrintInfo,
  isDarkMode,
  onError,
  onRendered,
  ...viewerOptions
}) => {
  const [isRendered, setIsRendered] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  const viewer = useRef(new StiViewer(undefined, 'StiViewer' + id, false));
  updateViewerOptions(viewer.current.options, viewerOptions);

  setLocalization(languages['uk']);

  useEffect(() => {
    if (language) {
      // В оригінальній лібі наплутано назва Localization.cultureName
      // насправді [lang].language
      const current = Localization.languages[Localization.cultureName];
      if (current.cultureName !== language) {
        const exist = Object.keys(Localization.languages).find(
          name => Localization.languages[name].cultureName === language
        );
        if (exist) {
          Localization.cultureName = Localization.languages[exist].cultureName;
        } else {
          setLocalization(languages[language]);
        }
      }
    }
  }, [language]);

  // Load template and data
  useEffect(() => {
    let isCurrent = true;
    const process = async (): Promise<void> => {
      if (reportTemplate && reportData) {
        const report = new Stimulsoft.Report.StiReport();
        try {
          report.load(reportTemplate);
          report.culture = report.culture ?? 'uk';
          if (showPrintInfo) {
            addPrintFooter(report, printedBy);
          }
          const dataSet = new Stimulsoft.System.Data.DataSet('ReportJSON');
          const serializedData = parseDates
            ? serializeDataForStiReport(
                typeof reportData === 'string'
                  ? JSON.parse(reportData)
                  : reportData
              )
            : reportData;
          dataSet.readJson(serializedData);

          report.dictionary.databases.clear();
          if (variables) {
            const serializedVariables = parseDates
              ? JSON.parse(serializeDataForStiReport(variables))
              : variables;
            Object.entries(serializedVariables).forEach(([key, value]) => {
              const reportVar = report.dictionary.variables.getByName(key);
              if (reportVar) {
                reportVar.value = String(value);
              }
            });
          }

          if (events) {
            Object.entries(events).forEach(([key, value]) => {
              if (value) {
                report[
                  key as keyof Pick<
                    Stimulsoft.Report.StiReport,
                    'onBeginProcessData'
                  >
                ] = eval(value);
              }
            });
          }

          report.regData('ReportJSON', 'ReportJSON', dataSet);

          await report.renderAsync2();
          if (!isCurrent) return;

          viewer.current.report = report;
          if (!isRendered) {
            viewer.current.renderHtml(ref.current ?? undefined);
            setIsRendered(true);
          }
          onRendered?.();
        } catch (error: any) {
          if (!isCurrent) return;

          // eslint-disable-next-line no-console
          console.error(error);
          if (onError) onError(error);
        }
      }
    };

    void process();
    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reportData,
    onError,
    reportTemplate,
    variables,
    events,
    printedBy,
    showPrintInfo,
    parseDates,
  ]);

  // Change view settings
  useEffect(() => {
    if (viewer.current?.jsObject) {
      updateViewerOptions(viewer.current.options, viewerOptions);
      viewer.current.refreshViewer();
    }
  }, [viewerOptions]);

  useEffect(() => {
    viewer.current.options.appearance.theme = isDarkMode
      ? Stimulsoft.Viewer.StiViewerTheme.Office2022BlackBlue
      : Stimulsoft.Viewer.StiViewerTheme.Office2022WhiteBlue;
  }, [isDarkMode]);

  return (
    <div
      id={`viewer${id}`}
      ref={ref}
      style={{ height: viewerOptions.height, width: viewerOptions.width }}
      tabIndex={tabIndex}
    />
  );
};
